'use memo';

import { dealNodeStatusValues, type DealDirection, type DealItem, type DealNodeStatus, type UpdateDealProgressInput } from '@standandforce/api-contract';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { Fragment, useState } from 'react';
import { Badge } from '@/components/Badge';
import Icon from '@/components/Icon/Icon';
import { HStack } from '@/components/Layout';
import { Text } from '@/components/Typography';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { CForm } from '@/features/c-form/CForm';
import { CFormSection } from '@/features/c-form/CFormSection/CFormSection';
import { defaultNS, type TFunction, useTranslation } from '@/lib/i18n';
import { useUpdateDealProgress, useUpdateFlowConfig } from '../-mutations/useDealMutations';
import { useGetDeal } from '../-queries/useGetDeal';
import type { DealFlowNode } from '../-types';

/** 保留ステージ。通常フローと別扱いで divider の後ろに表示する */
const HELD_STAGE_KEY = 'S98';
type ProgressDraft = Omit<UpdateDealProgressInput, 'id'>;
type TFn = TFunction;
function statusBadgeVariant(status: DealNodeStatus | null): 'neutralMuted' | 'primaryMuted' | 'mutedSuccess' {
  if (status === 'IN_PROGRESS') return 'primaryMuted';
  if (status === 'COMPLETED') return 'mutedSuccess';
  return 'neutralMuted';
}
function stageLabel(t: TFn, key: string): string {
  switch (key) {
    case 'S01':
      return t('flowStageS01');
    case 'S02':
      return t('flowStageS02');
    case 'S03':
      return t('flowStageS03');
    case 'S04':
      return t('flowStageS04');
    case 'S05':
      return t('flowStageS05');
    case 'S06':
      return t('flowStageS06');
    case 'S07':
      return t('flowStageS07');
    case 'S98':
      return t('flowStageS98');
    case 'S99':
      return t('flowStageS99');
    default:
      return key;
  }
}
function statusLabel(t: TFn, status: DealNodeStatus | null): string {
  switch (status ?? 'NOT_STARTED') {
    case 'NOT_STARTED':
      return t('dealNodeStatus.NOT_STARTED');
    case 'IN_PROGRESS':
      return t('dealNodeStatus.IN_PROGRESS');
    case 'COMPLETED':
      return t('dealNodeStatus.COMPLETED');
  }
}
type ProgressRow = {
  label: string;
  field: keyof ProgressDraft;
  stageKey: string;
};
function outboundRows(t: TFn): ProgressRow[] {
  return [{
    label: t('dealProgressNodeLead'),
    field: 'salesModeLeadStatus',
    stageKey: 'S01'
  }, {
    label: t('dealProgressNodeQuote'),
    field: 'salesModeQuoteStatus',
    stageKey: 'S02'
  }, {
    label: t('dealProgressNodeSalesOrder'),
    field: 'salesModeSalesOrderStatus',
    stageKey: 'S03'
  }, {
    label: t('dealProgressNodeContract'),
    field: 'salesModeContractStatus',
    stageKey: 'S04'
  }, {
    label: t('dealProgressNodeInvoice'),
    field: 'salesModeInvoiceStatus',
    stageKey: 'S06'
  }, {
    label: t('dealProgressNodeCollection'),
    field: 'salesModeCollectionStatus',
    stageKey: 'S07'
  }, {
    label: t('dealProgressNodeReceipt'),
    field: 'salesModeReceiptStatus',
    stageKey: 'S07'
  }, {
    label: t('dealProgressNodeAdvancesReceived'),
    field: 'salesModeAdvancesReceivedStatus',
    stageKey: 'S07'
  }];
}
function inboundRows(t: TFn): ProgressRow[] {
  return [{
    label: t('dealProgressNodeRfq'),
    field: 'prcModeRfqStatus',
    stageKey: 'S01'
  }, {
    label: t('dealProgressNodeSupplierQuote'),
    field: 'prcModeSupplierQuoteStatus',
    stageKey: 'S02'
  }, {
    label: t('dealProgressNodePurchaseRequest'),
    field: 'prcModePurchaseRequestStatus',
    stageKey: 'S03'
  }, {
    label: t('dealProgressNodePurchaseOrder'),
    field: 'prcModePurchaseOrderStatus',
    stageKey: 'S03'
  }, {
    label: t('dealProgressNodeVendorInvoice'),
    field: 'prcModeVendorInvoiceStatus',
    stageKey: 'S06'
  }, {
    label: t('dealProgressNodePayment'),
    field: 'prcModePaymentStatus',
    stageKey: 'S07'
  }, {
    label: t('dealProgressNodePaymentRun'),
    field: 'prcModePaymentRunStatus',
    stageKey: 'S07'
  }, {
    label: t('dealProgressNodeAdvancesPayment'),
    field: 'prcModeAdvancesPaymentStatus',
    stageKey: 'S07'
  }];
}
function parseNodes(flowConfig: Record<string, unknown>): DealFlowNode[] {
  const raw = flowConfig.nodes;
  if (!Array.isArray(raw)) return [];
  return (raw as DealFlowNode[]).filter(n => typeof n.order === 'number' && typeof n.key === 'string').sort((a, b) => a.order - b.order);
}
function dealToProgressDraft(deal: DealItem): ProgressDraft {
  return {
    salesModeLeadStatus: deal.salesModeLeadStatus,
    salesModeQuoteStatus: deal.salesModeQuoteStatus,
    salesModeSalesOrderStatus: deal.salesModeSalesOrderStatus,
    salesModeContractStatus: deal.salesModeContractStatus,
    salesModeInvoiceStatus: deal.salesModeInvoiceStatus,
    salesModeCollectionStatus: deal.salesModeCollectionStatus,
    salesModeReceiptStatus: deal.salesModeReceiptStatus,
    salesModeAdvancesReceivedStatus: deal.salesModeAdvancesReceivedStatus,
    prcModeRfqStatus: deal.prcModeRfqStatus,
    prcModeSupplierQuoteStatus: deal.prcModeSupplierQuoteStatus,
    prcModePurchaseRequestStatus: deal.prcModePurchaseRequestStatus,
    prcModePurchaseOrderStatus: deal.prcModePurchaseOrderStatus,
    prcModeVendorInvoiceStatus: deal.prcModeVendorInvoiceStatus,
    prcModePaymentStatus: deal.prcModePaymentStatus,
    prcModePaymentRunStatus: deal.prcModePaymentRunStatus,
    prcModeAdvancesPaymentStatus: deal.prcModeAdvancesPaymentStatus
  };
}
function progressFieldValue(deal: DealItem, field: keyof ProgressDraft): DealNodeStatus | null {
  return deal[field];
}
function DealFlowPage() {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.dealLedger'
  });
  const {
    dealId
  } = useParams({
    from: '/_staff/deal/$dealId/_layout'
  });
  const {
    data: deal
  } = useGetDeal(dealId);
  const {
    mutate: mutateFlowConfig,
    isPending: isFlowConfigPending
  } = useUpdateFlowConfig(dealId);
  const {
    mutate: mutateProgress,
    isPending: isProgressPending
  } = useUpdateDealProgress(dealId);
  const [isEditingFlow, setIsEditingFlow] = useState(false);
  const [flowDraft, setFlowDraft] = useState<DealFlowNode[]>([]);
  const [progressDraft, setProgressDraft] = useState<ProgressDraft | null>(null);
  const isEditingProgress = progressDraft !== null;
  const nodes = parseNodes(deal.flowConfig);
  function startEditFlow() {
    setFlowDraft(nodes.map(n => ({
      ...n
    })));
    setIsEditingFlow(true);
  }
  function cancelEditFlow() {
    setFlowDraft([]);
    setIsEditingFlow(false);
  }
  function toggleEnabled(key: string) {
    setFlowDraft(prev => prev.map(n => n.key === key ? {
      ...n,
      enabled: !n.enabled
    } : n));
  }
  function moveNode(index: number, direction: -1 | 1) {
    setFlowDraft(prev => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = prev.slice();
      [next[index], next[target]] = [next[target], next[index]];
      return next.map((n, i) => ({
        ...n,
        order: i + 1
      }));
    });
  }
  function saveEditFlow() {
    mutateFlowConfig(flowDraft, {
      onSuccess: () => {
        setIsEditingFlow(false);
        setFlowDraft([]);
      }
    });
  }
  function startEditProgress() {
    setProgressDraft(dealToProgressDraft(deal));
  }
  function cancelEditProgress() {
    setProgressDraft(null);
  }
  function setProgressField(key: keyof ProgressDraft, value: DealNodeStatus | null) {
    setProgressDraft(prev => prev ? {
      ...prev,
      [key]: value
    } : prev);
  }
  function saveEditProgress() {
    if (!progressDraft) return;
    mutateProgress(progressDraft, {
      onSuccess: () => {
        setProgressDraft(null);
      }
    });
  }
  const displayNodes = isEditingFlow ? flowDraft : nodes;

  // 非編集表示: 有効なステージのみ。保留(S98)は通常フローと分けて divider の後ろへ
  const enabledReadNodes = nodes.filter(n => n.enabled);
  const flowReadNodes = enabledReadNodes.filter(n => n.key !== HELD_STAGE_KEY);
  const heldReadNode = enabledReadNodes.find(n => n.key === HELD_STAGE_KEY);
  const flowActionButton = !isEditingFlow ? <Button variant="outline" size="sm" onClick={startEditFlow}>
      {t('flowTabButtonEdit')}
    </Button> : <HStack className="gap-2">
      <Button variant="outline" size="sm" onClick={cancelEditFlow} disabled={isFlowConfigPending}>
        {t('flowTabButtonCancel')}
      </Button>
      <Button size="sm" onClick={saveEditFlow} disabled={isFlowConfigPending}>
        {t('flowTabButtonSave')}
      </Button>
    </HStack>;
  const progressActionButton = !isEditingProgress ? <Button variant="outline" size="sm" onClick={startEditProgress}>
      {t('flowTabButtonEdit')}
    </Button> : <HStack className="gap-2">
      <Button variant="outline" size="sm" onClick={cancelEditProgress} disabled={isProgressPending}>
        {t('flowTabButtonCancel')}
      </Button>
      <Button size="sm" onClick={saveEditProgress} disabled={isProgressPending}>
        {t('flowTabButtonSave')}
      </Button>
    </HStack>;
  const enabledStageKeys = new Set(nodes.filter(n => n.enabled).map(n => n.key));
  const allRows: ProgressRow[] = deal.direction satisfies DealDirection === 'OUTBOUND' ? outboundRows(t) : inboundRows(t);
  const progressRows = allRows.filter(r => enabledStageKeys.has(r.stageKey));
  return <CForm editUnit="section" itemLayout="card">
      <CFormSection id="deal-flow-config" label={t('flowStageSectionTitle')} hideActionButtons actionButton={flowActionButton}>
        <div className="col-span-full">
          {isEditingFlow ? displayNodes.length === 0 ? <Text as="p" variant="labelSmall" className="text-neutral-on-muted-fill">
                {t('flowTabNoConfig')}
              </Text> : <div className="rounded-md border border-neutral-muted-border">
                {displayNodes.map((node, index) => <div key={node.key} className="flex items-center gap-4 px-4 py-3 border-b border-neutral-muted-border last:border-0">
                    <HStack className="gap-1 shrink-0">
                      <Button variant="outline" size="sm" onClick={() => moveNode(index, -1)} disabled={index === 0} aria-label={t('flowMoveUp', {
                stage: stageLabel(t, node.key)
              })}>
                        ↑
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => moveNode(index, 1)} disabled={index === displayNodes.length - 1} aria-label={t('flowMoveDown', {
                stage: stageLabel(t, node.key)
              })}>
                        ↓
                      </Button>
                    </HStack>
                    <Text as="span" variant="label" className="flex-1">
                      {stageLabel(t, node.key)}
                    </Text>
                    <Switch checked={node.enabled} onCheckedChange={() => toggleEnabled(node.key)} />
                  </div>)}
              </div> : flowReadNodes.length === 0 && !heldReadNode ? <Text as="p" variant="labelSmall" className="text-neutral-on-muted-fill">
              {t('flowTabNoConfig')}
            </Text> : <div className="flex flex-wrap items-center gap-2">
              {flowReadNodes.map((node, index) => <Fragment key={node.key}>
                  {index > 0 && <Icon iconName="ArrowRightIcon" className="h-4 w-4 shrink-0 text-neutral-on-muted-fill" />}
                  <Badge variant="primary" className="px-3 py-1 text-label">
                    {stageLabel(t, node.key)}
                  </Badge>
                </Fragment>)}
              {heldReadNode && <>
                  {flowReadNodes.length > 0 && <div aria-hidden="true" className="mx-1 h-6 shrink-0 border-l border-neutral-muted-border" />}
                  <Badge variant="primary" className="px-3 py-1 text-label">
                    {stageLabel(t, heldReadNode.key)}
                  </Badge>
                </>}
            </div>}
        </div>
      </CFormSection>
      <CFormSection id="deal-progress" label={t('dealProgressSectionTitle')} hideActionButtons actionButton={progressActionButton}>
        <div className="col-span-full">
          {progressRows.length === 0 ? <Text as="p" variant="labelSmall" className="text-neutral-on-muted-fill">
              {t('dealProgressNoVisibleNodes')}
            </Text> : <div className="grid grid-cols-3 gap-4">
              {progressRows.map(row => {
            const status = progressFieldValue(deal, row.field);
            const draftStatus = progressDraft?.[row.field] ?? null;
            return <div key={row.field} className="flex flex-col items-start gap-2 rounded-lg border border-neutral-muted-border bg-neutral-fill p-4">
                    <Text as="span" variant="label">
                      {row.label}
                    </Text>
                    {isEditingProgress ? <Select value={draftStatus ?? 'NOT_STARTED'} onValueChange={v => setProgressField(row.field, v as DealNodeStatus)}>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {dealNodeStatusValues.map(s => <SelectItem key={s} value={s}>
                              {statusLabel(t, s)}
                            </SelectItem>)}
                        </SelectContent>
                      </Select> : <Badge variant={statusBadgeVariant(status)}>
                        {statusLabel(t, status)}
                      </Badge>}
                  </div>;
          })}
            </div>}
        </div>
      </CFormSection>
    </CForm>;
}
export const Route = createFileRoute('/_staff/deal/$dealId/_layout/flow')({
  component: DealFlowPage
});